import { Info, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { EnterpriseContactData } from "./types";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useSendEnterpriseContactMessage } from "./api/send-contact-info-mutation";
import { useReCaptcha } from "../../hooks/useReCaptcha";
import { EnterpriseContactSuccess } from "./enterprise-contact-success";

const contactFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Invalid email address"),
  tel: z.string().optional(),
  company: z.string().min(1, "Company is required"),
  useCase: z.string().min(1, "Use case is required"),
});

interface ContactUsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: EnterpriseContactData) => void;
  data?: EnterpriseContactData;
}

export function EnterpriseContactModal({ isOpen, onSubmit, onClose, data }: ContactUsModalProps) {
  const { mutateAsync: sendEnterpriseContactMessage } = useSendEnterpriseContactMessage();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof contactFormSchema>>({
    resolver: zodResolver(contactFormSchema),
  });
  const [error, setError] = useState<string | null>();
  const [isSuccess, setIsSuccess] = useState(false);
  const [timestamp] = useState(() => Date.now().toString());

  useEffect(() => {
    if (data) {
      reset({ ...data });
    }
  }, [data, reset]);

  const { executeReCaptcha, error: recaptchaError } = useReCaptcha({
    action: "enterprise_contact_form_submit",
  });
  const handleFormSubmit = async (formData: z.infer<typeof contactFormSchema>) => {
    setError(null);

    try {
      // Get reCAPTCHA token using our hook
      const token = await executeReCaptcha();

      // Handle reCAPTCHA error
      if (recaptchaError) {
        setError(`reCAPTCHA error: ${recaptchaError}`);
        return;
      }

      if (!token) {
        setError("Failed to verify that you are not a robot. Please try again later.");
        return;
      }
      const dataToSubmit = { origin: "web", ...data, ...formData, recaptchaToken: token, timestamp };
      await sendEnterpriseContactMessage({ data: dataToSubmit });
      onSubmit(dataToSubmit);

      // Set success state
      setIsSuccess(true);
    } catch (error) {
      console.error(error);
      setError(`An error occurred while submitting the form. Please try again later.`);
    }
  };

  const isFieldReadonly = (fieldName: keyof EnterpriseContactData) => {
    return data && !!data[fieldName];
  };

  const handleReset = () => {
    setIsSuccess(false);
    setError(null);
    reset();
  };

  // If form was successfully submitted, show success component
  if (isSuccess) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md sm:max-w-2xl lg:max-w-4xl max-h-[90vh] overflow-y-auto bg-card">
          <EnterpriseContactSuccess onReset={handleReset} onClose={onClose} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md sm:max-w-2xl lg:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <Info className="h-6 w-6 text-primary" />
            <DialogTitle>Contact Us</DialogTitle>
          </div>
          <DialogDescription>Get in touch with our team to discuss your enterprise needs.</DialogDescription>
        </DialogHeader>

        {/* Error Display */}
        {error && (
          <Alert variant="destructive">
            <Info className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>{error}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setError(null)}
                className="h-auto p-1 hover:bg-destructive/20"
              >
                <X className="h-4 w-4" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-8">
          {/* Honeypot field - hidden from users but visible to bots */}
          <input type="text" name="website" style={{ display: "none" }} tabIndex={-1} autoComplete="off" />

          {/* Timestamp field for security - hidden from users */}
          <input type="hidden" name="timestamp" value={timestamp} />

          {/* Contact Information Section */}
          <div className="space-y-4">
            <div className="border-b border-border pb-2">
              <h3 className="text-lg font-semibold text-foreground">Contact Information</h3>
              <p className="text-sm text-muted-foreground">Tell us about yourself and your organization</p>
            </div>

            {/* First row - Name and Email */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  {...register("name")}
                  readOnly={isFieldReadonly("name")}
                  className={isFieldReadonly("name") ? "bg-muted" : ""}
                />
                {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  {...register("email")}
                  readOnly={isFieldReadonly("email")}
                  className={isFieldReadonly("email") ? "bg-muted" : ""}
                />
                {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
              </div>
            </div>

            {/* Second row - Phone and Company */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tel">Phone (optional)</Label>
                <Input
                  id="tel"
                  type="tel"
                  {...register("tel")}
                  readOnly={isFieldReadonly("tel")}
                  className={isFieldReadonly("tel") ? "bg-muted" : ""}
                />
                {errors.tel && <p className="text-sm text-destructive">{errors.tel.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="company">Company</Label>
                <Input
                  id="company"
                  {...register("company")}
                  readOnly={isFieldReadonly("company")}
                  className={isFieldReadonly("company") ? "bg-muted" : ""}
                />
                {errors.company && <p className="text-sm text-destructive">{errors.company.message}</p>}
              </div>
            </div>
          </div>

          {/* Describe Your Needs Section */}
          <div className="space-y-4">
            <div className="border-b border-border pb-2">
              <h3 className="text-lg font-semibold text-foreground">Describe Your Needs</h3>
              <p className="text-sm text-muted-foreground">Help us understand your requirements and use case</p>
            </div>

            {/* Use Case - Full width */}
            <div className="space-y-2">
              <Label htmlFor="useCase">Use Case</Label>
              <Textarea
                id="useCase"
                {...register("useCase")}
                readOnly={isFieldReadonly("useCase")}
                className={isFieldReadonly("useCase") ? "bg-muted" : ""}
                rows={4}
                placeholder="Please describe your use case and specific requirements..."
              />
              {errors.useCase && <p className="text-sm text-destructive">{errors.useCase.message}</p>}
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <div className="w-full text-xs text-muted-foreground mt-2">
              This site is protected by reCAPTCHA and the Google{" "}
              <a
                className="underline"
                href="https://policies.google.com/privacy"
                rel="noopener noreferrer"
                target="_blank"
              >
                {" "}
                Privacy Policy
              </a>{" "}
              and{" "}
              <a
                className="underline"
                href="https://policies.google.com/terms"
                rel="noopener noreferrer"
                target="_blank"
              >
                Terms of Service
              </a>{" "}
              apply.
            </div>

            <Button variant="outline" onClick={onClose} type="button" disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
